// import React, { useEffect, useState } from 'react'
import {
    Clock,
    CalendarDays,
    BadgeIndianRupee,
    Network,
    UserCheck,
    ClipboardCheck,
    HeartPulse,
    ShieldCheck,
    ArrowUpRight,
} from 'lucide-react'

// const STATS = [
//     { label: 'Active Employees', value: '200+' },
//     { label: 'Departments', value: '8+' },
//     { label: 'Branches', value: '3+' },
// ]

const FEATURES = [
    {
        icon: Clock,
        title: 'Attendance Tracking',
        desc: 'Real-time daily and monthly attendance with geo-fencing support.',
    },
    {
        icon: CalendarDays,
        title: 'Leave & WFH Management',
        desc: 'Multi-level approval chains for leave, WFH, and OD requests.',
    },
    {
        icon: BadgeIndianRupee,
        title: 'Payroll & Payslips',
        desc: 'Configurable salary heads with automated payslip generation.',
    },
    {
        icon: Network,
        title: 'Org Hierarchy',
        desc: 'Interactive org charts showing department and reporting structures.',
    },
    {
        icon: ClipboardCheck,
        title: 'Regularization',
        desc: 'Missed punch corrections with manager review workflows.',
    },
    {
        icon: ShieldCheck,
        title: 'Role-Based Access',
        desc: 'Granular designation-level permissions across all modules.',
    },
]

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {


    return (
        <div className="min-h-screen w-full flex bg-[#f8fafc] relative overflow-hidden">
            <div className="flex w-full min-h-screen">
                {/* ─── Left Panel ─── */}
                <div className="hidden lg:flex w-[55%] flex-col relative overflow-hidden bg-linear-to-br from-[#0c1222] via-[#0f172a] to-[#0c1222]">
                    {/* Ambient glow orbs */}
                    <div className="absolute top-[-8%] right-[15%] w-[500px] h-[500px] rounded-full bg-cyan-500/[0.07] blur-[130px]" />
                    <div className="absolute bottom-[10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-blue-600/6 blur-[120px]" />
                    <div className="absolute top-[40%] right-[-10%] w-[300px] h-[300px] rounded-full bg-teal-400/5 blur-[100px]" />

                    {/* Subtle grid */}
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-size-[64px_64px]" />

                    {/* Content wrapper */}
                    <div className="relative z-10 flex flex-col justify-between h-full p-12 xl:p-16">
                        {/* ── Header ── */}
                        <div className={`flex items-center gap-3.5 transition-all duration-700 opacity-100 translate-y-0`}>
                            <div className="h-11 w-11 rounded-xl bg-linear-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                                <HeartPulse size={22} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold tracking-tight text-white leading-none">
                                    Shri Sadguru Seva Sangh Trust HRMS
                                </h1>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mt-0.5">
                                    Human Resource Management
                                </p>
                            </div>
                        </div>

                        {/* ── Main content ── */}
                        <div className={`max-w-xl space-y-10 transition-all duration-700 delay-150 opacity-100 translate-y-0`}>
                            {/* Heading */}
                            <div className="space-y-5">
                                <h2 className="text-4xl xl:text-[2.75rem] font-extrabold text-white leading-[1.15] tracking-tight">
                                    Streamline your<br />
                                    <span className="bg-linear-to-r from-cyan-400 via-teal-300 to-blue-400 bg-clip-text text-transparent">
                                        workforce
                                    </span>
                                    <br />management.
                                </h2>
                                <p className="text-[15px] text-slate-400 leading-relaxed max-w-md">
                                    A complete platform for Sadguru Trust — managing attendance, leaves, payroll, and organizational structure from one unified dashboard.
                                </p>
                            </div>

                            {/* Stats row */}
                            {/* <div className="flex items-center gap-8">
                                {STATS.map((stat, i) => (
                                    <div key={i} className="flex flex-col">
                                        <span className="text-2xl font-black text-white tracking-tight">{stat.value}</span>
                                        <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider mt-0.5">{stat.label}</span>
                                    </div>
                                ))}
                            </div> */}

                            {/* Features grid */}
                            <div className="grid grid-cols-2 gap-3">
                                {FEATURES.map((feat, i) => {
                                    const Icon = feat.icon
                                    return (
                                        <div
                                            key={i}
                                            className="group flex items-start gap-3 p-3.5 rounded-xl bg-white/3 border border-white/6 hover:bg-white/6 hover:border-cyan-500/20 transition-all duration-300"
                                        >
                                            <div className="h-9 w-9 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/15 transition-colors">
                                                <Icon size={16} strokeWidth={2} />
                                            </div>
                                            <div className="min-w-0 pt-0.5">
                                                <div className="flex items-center gap-1.5">
                                                    <h3 className="text-[13px] font-semibold text-slate-200 leading-tight">{feat.title}</h3>
                                                    <ArrowUpRight size={11} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity -mt-0.5" />
                                                </div>
                                                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{feat.desc}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {/* ── Footer ── */}
                        <div className={`flex items-center justify-between transition-all duration-700 delay-300 opacity-100 translate-y-0`}>
                            <p className="text-[11px] text-slate-600 font-medium">
                                © {new Date().getFullYear()} Sadguru Trust
                            </p>
                            <div className="flex items-center gap-1.5">
                                <UserCheck size={13} className="text-emerald-500" />
                                <span className="text-[11px] text-slate-500 font-medium">Secure admin portal</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─── Right Panel: Auth Form ─── */}
                <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-10 relative bg-white">
                    {/* Decorative corner shapes */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-linear-to-bl from-cyan-50 to-transparent rounded-bl-full opacity-60 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-linear-to-tr from-slate-50 to-transparent rounded-tr-full opacity-60 pointer-events-none" />

                    {/* Mobile logo (only on small screens) */}
                    <div className="lg:hidden absolute top-8 left-6 flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-lg bg-linear-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                            <HeartPulse size={18} className="text-white" />
                        </div>
                        <span className="text-lg font-bold text-slate-900 tracking-tight">Sadguru HRMS</span>
                    </div>

                    <div className="w-full max-w-[420px] relative z-10">
                        {/* Visual aura behind login card */}
                        <div className="absolute w-[400px] h-[400px] bg-cyan-200/20 blur-3xl rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>

                        <div className="w-full max-w-md relative z-10">
                            {/* Glassmorphic Card Container */}
                            <div className="bg-white/95 backdrop-blur-2xl rounded-3xl border border-slate-200/60 p-8 sm:p-12 shadow-[0_24px_50px_-12px_rgba(8,112,184,0.06)] relative overflow-hidden">
                                {/* Top decorative gradient bar */}
                                <div className="absolute top-0 left-0 right-0 h-[3px] bg-linear-to-r from-cyan-400 to-blue-500"></div>
                                {children}
                            </div>
                        </div>

                        {/* Footer text below card */}
                        <p className="text-center text-[11px] text-slate-400 mt-6 font-medium lg:hidden">
                            © {new Date().getFullYear()} Sadguru Medical Trust
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AuthLayout
